// Shared shell token contract for the authenticated 4-zone shell (top command bar,
// left navigation, center canvas, optional right context rail).
//
// The institution token set is the registrar / archive-office surface moved verbatim
// from InstitutionDashboardShell so the shell primitives and the sidebar keep reading
// the exact same values. The individual (night-aurora) variant reuses the exact topbar
// gradient/border and accent colors DashboardLayout already used before Stage 1b, so
// swapping to the shared TopCommandBar/ContextRail introduces no visual regression.

import {
  INSTITUTION_HERITAGE_LIGHT,
  INSTITUTION_OPALESCENT_EDGE,
  INSTITUTION_PEARL_REFLECTION,
  INSTITUTION_SHELL_LIGHT,
  INSTITUTION_SHELL_DARK,
} from '@/lib/institution-heritage-palette';

export type ShellTokens = {
  bg: string;
  // Primary workspace surface (level 3): the content canvas behind sidebar + rail.
  // Individual sets this equal to `bg` (unchanged appearance); institution gives it a
  // subtle directional gradient so the canvas reads as a distinct layer from the
  // outer page background instead of repeating the same flat color.
  canvasBg: string;
  sidebarBg: string;
  sidebarBorder: string;
  surface: string;
  // Elevated record/action surface (level 4): cards, hero, popovers. Institution
  // gives this its own opal/charcoal tone distinct from `surface`; individual keeps
  // it identical to `surface` so nothing shifts visually there.
  surfaceElevated: string;
  surfaceHover: string;
  // Inset metadata surface (level 5): recessed chips, stat pills, id/date rows.
  surfaceInset: string;
  border: string;
  textPrimary: string;
  textMuted: string;
  textSub: string;
  accent: string;
  accentSoft: string;
  accentText: string;
  secondary: string;
  activeBg: string;
  activeBorder: string;
  activeShadow: string;
  activeText: string;
  topbar: string;
  topbarBorder: string;
  sectionLabel: string;
  // Contextual inspector surface (level 6): the right rail's own background/border,
  // intentionally distinct from `sidebarBg` on institution so the rail no longer
  // reads as "the same panel repeated twice." Individual keeps it equal to
  // `sidebarBg`, matching ContextRail's existing behavior exactly.
  railSurface: string;
  railBorder: string;
  // Thin divider between rail sections / stacked metadata rows.
  dividerSoft: string;
  // Restrained prismatic/spectral edge treatment for masthead surfaces. 'none' on
  // individual (no visual change); a soft multi-stop gradient on institution.
  prismEdge: string;
};

// Institution Console tokens — a registrar / archive-office surface, not a generic
// AI-SaaS dashboard. Light/opal mode layers five pearl tiers across the sidebar, app
// canvas, content, cards, and context rail. Violet anchors identity, aqua carries
// operational accents, champagne marks premium/archive moments, blush is a quiet
// community accent, and green is limited to verified/success semantics. Dark mode's
// established institutional aurora is intentionally unchanged.
export function makeInstitutionShellTokens(isOpal: boolean): ShellTokens {
  return isOpal
    ? {
        // Five distinguishable pearl tiers: app canvas < content < cards, with sidebar
        // and rail carrying their own restrained reflected-light atmosphere.
        bg: INSTITUTION_HERITAGE_LIGHT.appBg,
        canvasBg: `${INSTITUTION_PEARL_REFLECTION},linear-gradient(180deg,#FAFAFC 0%,#F7F7FA 100%)`,
        // Sidebar and topbar have their own cooler pearl reflection; the content plane
        // stays quieter so cards retain hierarchy.
        sidebarBg: INSTITUTION_SHELL_LIGHT.sidebarBg,
        sidebarBorder: INSTITUTION_SHELL_LIGHT.border,
        surface: INSTITUTION_HERITAGE_LIGHT.cardBg,
        surfaceElevated: INSTITUTION_HERITAGE_LIGHT.cardBg,
        surfaceHover: INSTITUTION_HERITAGE_LIGHT.mutedSurface,
        surfaceInset: INSTITUTION_HERITAGE_LIGHT.mutedSurface,
        border: INSTITUTION_HERITAGE_LIGHT.border,
        textPrimary: INSTITUTION_HERITAGE_LIGHT.textPrimary,
        textMuted: INSTITUTION_HERITAGE_LIGHT.textSecondary,
        textSub: INSTITUTION_HERITAGE_LIGHT.textMuted,
        accent: INSTITUTION_HERITAGE_LIGHT.violet,
        accentSoft: 'rgba(200,182,255,0.22)',
        accentText: INSTITUTION_HERITAGE_LIGHT.violetDeep,
        secondary: INSTITUTION_HERITAGE_LIGHT.goldDeep,
        // A quiet lavender-opal selection with a violet edge keeps navigation clear
        // without turning the shell into a painted gradient.
        activeBg: INSTITUTION_SHELL_LIGHT.lavender,
        activeBorder: '#8D79BD',
        activeShadow: '0 4px 14px rgba(113,90,165,0.08)',
        activeText: INSTITUTION_HERITAGE_LIGHT.violetDeep,
        topbar: INSTITUTION_SHELL_LIGHT.topbar,
        // The outer frame edge (sidebar right border, topbar bottom border) is a
        // visibly stronger violet-neutral line than ordinary internal dividers.
        topbarBorder: INSTITUTION_SHELL_LIGHT.edgeBorder,
        sectionLabel: INSTITUTION_SHELL_LIGHT.sectionLabel,
        railSurface:
          'radial-gradient(circle at 18% 8%,rgba(200,182,255,0.14),transparent 32%),radial-gradient(circle at 92% 68%,rgba(169,229,234,0.1),transparent 36%),rgba(250,250,252,0.94)',
        railBorder: 'rgba(112,88,164,0.24)',
        dividerSoft: INSTITUTION_HERITAGE_LIGHT.divider,
        prismEdge: INSTITUTION_OPALESCENT_EDGE,
      }
    : {
        bg: '#0a0e15',
        canvasBg: 'linear-gradient(180deg,#0c111a 0%,#090d13 100%)',
        // Same shell-only aurora treatment as light mode, translated into a midnight
        // register: emerald + indigo blooms with a whisper of restrained gold, over a
        // deep navy base -- see INSTITUTION_SHELL_DARK.
        sidebarBg: INSTITUTION_SHELL_DARK.sidebarBg,
        sidebarBorder: INSTITUTION_SHELL_DARK.border,
        surface: '#151b25',
        surfaceElevated: '#151b25',
        surfaceHover: '#1b232c',
        surfaceInset: '#121720',
        border: 'rgba(255,255,255,0.08)',
        textPrimary: '#e7e2d4',
        textMuted: '#8b93a0',
        textSub: '#5f6774',
        accent: '#3fae8e',
        accentSoft: 'rgba(63,174,142,0.12)',
        // White (not tinted teal) so the active nav label is unmistakably legible
        // against the dark emerald-tinted selected surface.
        accentText: '#ffffff',
        // Bright, shiny gold -- the same family as the light shell's gold, since gold
        // doesn't need to change per theme to read as premium.
        secondary: '#D4AF5C',
        activeBg: 'rgba(23,76,66,0.4)',
        // A plain hex (not rgba) -- this is also used verbatim, with no alpha
        // adjustment, as a solid border by the shared TopCommandBar/ShellCreateMenu
        // primitives, so it must stay opaque-ready rather than pre-translucent.
        activeBorder: '#2F9B84',
        activeShadow: 'none',
        activeText: '#ffffff',
        topbar: INSTITUTION_SHELL_DARK.topbar,
        topbarBorder: INSTITUTION_SHELL_DARK.edgeBorder,
        sectionLabel: INSTITUTION_SHELL_DARK.sectionLabel,
        railSurface: '#12151f',
        railBorder: 'rgba(169,150,214,0.14)',
        dividerSoft: 'rgba(255,255,255,0.07)',
        prismEdge:
          'linear-gradient(90deg,rgba(169,150,214,0.35) 0%,rgba(110,168,216,0.3) 33%,rgba(63,174,142,0.3) 66%,rgba(201,154,75,0.3) 100%)',
      };
}

// Individual (night-aurora) shell tokens — a personal, editorial surface: teal/cyan
// accent, indigo-violet depth in dark mode, warm opal-white in light mode. Colors are
// pulled directly from DashboardLayout's pre-Stage-1b header/sidebar so the shared
// TopCommandBar and ContextRail read as a continuation of the existing individual
// identity, not a new one. The new level-4/5/6 fields intentionally mirror existing
// fields 1:1 so the individual dashboard and Messages rail render pixel-identical to
// before this token set gained institution's surface-hierarchy fields.
export function makeIndividualShellTokens(isOpal: boolean): ShellTokens {
  return isOpal
    ? {
        bg: '#f6f7fb',
        canvasBg: '#f6f7fb',
        sidebarBg: '#ffffff',
        sidebarBorder: 'rgba(0,0,0,0.08)',
        surface: '#ffffff',
        surfaceElevated: '#ffffff',
        surfaceHover: '#eef2f9',
        surfaceInset: '#eef2f9',
        border: 'rgba(0,0,0,0.09)',
        textPrimary: '#0f172a',
        textMuted: '#64748b',
        textSub: '#94a3b8',
        accent: '#0d9488',
        accentSoft: 'rgba(13,148,136,0.08)',
        accentText: '#0d9488',
        secondary: '#7c3aed',
        activeBg: 'rgba(13,148,136,0.08)',
        activeBorder: '#0d9488',
        activeShadow: 'none',
        activeText: '#0d9488',
        topbar: 'rgba(255,255,255,0.97)',
        topbarBorder: 'rgba(0,0,0,0.08)',
        sectionLabel: '#64748b',
        railSurface: '#ffffff',
        railBorder: 'rgba(0,0,0,0.09)',
        dividerSoft: 'rgba(0,0,0,0.06)',
        prismEdge: 'none',
      }
    : {
        bg: '#06101e',
        // Primary workspace surface: a restrained atmosphere of emerald, violet, and
        // very soft rose over a deep navy-indigo base -- not a rainbow wash, just a
        // whisper of each hue at the corners so the canvas doesn't read as flat.
        canvasBg:
          'radial-gradient(120% 70% at 12% -8%,rgba(16,185,129,0.045) 0%,transparent 46%),radial-gradient(100% 60% at 100% 0%,rgba(167,139,250,0.04) 0%,transparent 46%),radial-gradient(90% 65% at 55% 108%,rgba(236,72,153,0.024) 0%,transparent 52%),linear-gradient(175deg,#091728 0%,#071321 55%,#06101e 100%)',
        sidebarBg: '#0d1f35',
        sidebarBorder: 'rgba(255,255,255,0.1)',
        surface: '#111f36',
        surfaceElevated: '#111f36',
        surfaceHover: '#16283f',
        surfaceInset: '#16283f',
        border: 'rgba(255,255,255,0.1)',
        textPrimary: '#f8fafc',
        textMuted: '#94a3b8',
        textSub: '#64748b',
        accent: '#22d3ee',
        accentSoft: 'rgba(34,211,238,0.12)',
        accentText: '#5eead4',
        secondary: '#a78bfa',
        activeBg: 'rgba(34,211,238,0.12)',
        activeBorder: '#22d3ee',
        activeShadow: 'none',
        activeText: '#5eead4',
        topbar:
          'linear-gradient(135deg,rgba(12,25,43,0.98) 0%,rgba(17,30,51,0.97) 50%,rgba(10,29,40,0.98) 100%)',
        topbarBorder: 'rgba(103,191,178,0.24)',
        sectionLabel: '#7f97ab',
        // Contextual inspector surface: a touch deeper/cooler than the sidebar so the
        // rail reads as its own layer in the surface hierarchy, not a repeated panel.
        railSurface: 'linear-gradient(165deg,#0e1c33 0%,#0b1526 100%)',
        railBorder: 'rgba(167,139,250,0.16)',
        dividerSoft: 'rgba(255,255,255,0.06)',
        // Restrained edge-lighting for important containers (the rail header) --
        // emerald to violet to very soft rose, the same three-accent family as canvasBg.
        prismEdge:
          'linear-gradient(90deg,rgba(16,185,129,0.4) 0%,rgba(167,139,250,0.35) 50%,rgba(236,72,153,0.25) 100%)',
      };
}
